/* ============================================================
   Scheduling helpers for non-essential third-party scripts.

   `requestIdleCallback` fires within a few hundred ms of `load` on a page that
   settles quickly, which is still inside the browser's (and Lighthouse's) LCP
   window — a 183 KB analytics payload landing there competes with the page's
   own critical work. Anything that isn't needed to render the page is deferred
   to the visitor's first interaction instead, with a timeout so scripts that
   must run for every visit still run.
   ============================================================ */

const INTERACTIONS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const

/** Run `cb` once the page has loaded and the browser is idle. */
export function whenIdle(cb: () => void, timeout = 5000): void {
  if (typeof window === 'undefined') return

  const schedule = () => {
    const w = window as any
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(cb, { timeout })
    else setTimeout(cb, 1000)
  }

  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
}

/**
 * Run `cb` on the visitor's first interaction, or `fallbackMs` after load —
 * whichever happens first. Returns a canceller.
 */
export function onFirstInteraction(cb: () => void, fallbackMs = 5000): () => void {
  if (typeof window === 'undefined') return () => {}

  let done = false
  let timerId: ReturnType<typeof setTimeout> | undefined

  const cleanup = () => {
    INTERACTIONS.forEach((evt) => window.removeEventListener(evt, run))
    if (timerId !== undefined) clearTimeout(timerId)
  }

  function run() {
    if (done) return
    done = true
    cleanup()
    cb()
  }

  INTERACTIONS.forEach((evt) => window.addEventListener(evt, run, { passive: true, once: true }))

  const startTimer = () => {
    if (!done) timerId = setTimeout(run, fallbackMs)
  }

  if (document.readyState === 'complete') startTimer()
  else window.addEventListener('load', startTimer, { once: true })

  return () => {
    done = true
    cleanup()
  }
}
